import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BRAND } from "@/lib/brand";

const Section = ({ n, title, children }) => (
  <section className="border-t border-white/10 py-10">
    <div className="grid md:grid-cols-12 gap-6">
      <div className="md:col-span-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
          Chapter {n.toString().padStart(2, "0")}
        </div>
        <h2 className="font-display font-bold text-white text-xl md:text-2xl mt-2 tracking-tight leading-tight">
          {title}
        </h2>
      </div>
      <div className="md:col-span-9 space-y-4 text-zinc-300 text-[15px] leading-relaxed">
        {children}
      </div>
    </div>
  </section>
);

const P = ({ children }) => <p>{children}</p>;
const UL = ({ items }) => (
  <ul className="space-y-2 pl-1">
    {items.map((t, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="text-[#E60000] mt-1">·</span>
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export default function Terms() {
  return (
    <main data-testid="terms-page" className="bg-[#050505] text-white">
      <Header />
      <div className="pt-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E60000] font-bold">
            MHJ Prestige Transport Ltd
          </div>
          <h1 className="font-display font-extrabold text-white text-4xl md:text-6xl leading-[1.0] tracking-tight mt-4">
            Terms &amp; Conditions
          </h1>
          <p className="text-zinc-400 text-sm mt-4 max-w-2xl">
            Of vehicle transport services. These apply to all quotations, bookings and jobs
            arranged or provided by MHJ Prestige Transport Ltd.
          </p>

          <Section n={1} title="About Us">
            <P>
              These Terms &amp; Conditions apply to vehicle transport services arranged or provided by
              MHJ Prestige Transport Ltd (&ldquo;MHJ&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;).
              Customers should ensure that all information supplied when requesting or booking transport is
              complete and accurate.
            </P>
            <P>
              Contact: <a className="underline" href={`mailto:${BRAND.email}`}>{BRAND.email}</a> ·{" "}
              <a className="underline" href={`tel:${BRAND.phoneRaw}`}>{BRAND.phone}</a>. Based in
              {" "}{BRAND.location}.
            </P>
          </Section>

          <Section n={2} title="Quotations">
            <P>
              Instant quotations are calculated using the information supplied by the customer,
              including collection and delivery locations, vehicle details, vehicle condition, requested
              date and time, accessibility and any additional requirements.
            </P>
            <P>
              Where an Instant Booking Price is displayed, that price applies when the customer completes
              the online booking and pays the required booking deposit.
            </P>
            <P>A Standard Guaranteed Quote is normally valid for 24 hours unless stated otherwise.</P>
            <P>A quotation may require amendment where information supplied is materially inaccurate. Examples include, but are not limited to:</P>
            <UL items={[
              "Incorrect vehicle dimensions or type",
              "A vehicle stated to be running which is actually a non-runner",
              "Seized wheels or drivetrain",
              "Undisclosed accident damage affecting loading",
              "Restricted or unsafe access",
              "Incorrect collection or delivery address",
              "Additional loading requirements",
              "Material changes requested by the customer after booking",
            ]}/>
            <P>Any proposed change in price will be explained to the customer before additional work is undertaken where reasonably possible.</P>
          </Section>

          <Section n={3} title="Booking Deposit">
            <P>A booking deposit is required to secure an online booking.</P>
            <P>
              The website normally requests approximately 30% of the agreed transport price, with the
              deposit rounded upward to the nearest £10. The deposit forms part of the total transport
              price and is deducted from the remaining balance.
            </P>
            <P>
              If MHJ is unable to fulfil or secure the requested transport, any booking deposit paid for
              that booking will be refunded in full.
            </P>
          </Section>

          <Section n={4} title="Customer Cancellation">
            <P>
              If the customer cancels a confirmed booking, MHJ may retain or recover an amount reasonably
              reflecting losses directly caused by that cancellation, including reasonable administrative
              costs, costs already incurred, transporter allocation costs and loss resulting from reserved
              transport capacity where applicable.
            </P>
            <P>MHJ will not impose a cancellation charge which exceeds its reasonably incurred loss.</P>
            <P>
              Where the amount retained is less than the deposit paid, any remaining refundable amount
              will be returned to the customer. Where required by applicable consumer law, statutory
              cancellation rights remain unaffected.
            </P>
            <P>Nothing in these Terms removes or reduces any rights the customer has under applicable UK consumer law.</P>
          </Section>

          <Section n={5} title="Cancellation by MHJ">
            <P>If MHJ cannot provide or secure the agreed transport service, MHJ may cancel the booking.</P>
            <P>
              Where MHJ cancels before the transport service is carried out and the cancellation is not
              caused by a breach or failure by the customer, payments relating to services not supplied
              will be refunded as appropriate.
            </P>
            <P>MHJ will try to notify the customer as quickly as reasonably possible.</P>
          </Section>

          <Section n={6} title="Collection and Delivery Times">
            <P>Collection and delivery dates and time windows are arranged in good faith.</P>
            <P>Road transport can be affected by circumstances including:</P>
            <UL items={[
              "Traffic, road closures, accidents",
              "Weather",
              "Vehicle breakdown",
              "Ferry or port delays",
              "Loading delays",
              "Previous customer delays",
              "Legal driving-hour restrictions",
              "Events outside reasonable control",
            ]}/>
            <P>
              Unless expressly agreed otherwise in writing, arrival times should be treated as agreed
              collection or delivery windows rather than guarantees to arrive at a precise minute.
            </P>
          </Section>

          <Section n={7} title="Vehicle Condition">
            <P>The customer must accurately describe the condition of the vehicle.</P>
            <UL items={[
              "A “Runner” should be capable of starting, moving, steering and braking sufficiently for normal loading.",
              "A “Non-runner — wheels roll freely” should have wheels which rotate sufficiently for winching/loading and should be capable of being safely steered where required.",
              "Vehicles with seized wheels, seized drivetrain, locked transmission or other loading difficulties require prior disclosure.",
            ]}/>
            <P>Additional reasonable charges may apply if a vehicle materially differs from the condition declared during booking.</P>
          </Section>

          <Section n={8} title="Low and Modified Vehicles">
            <P>
              Customers should disclose lowered suspension, very low ground clearance, unusual bodywork,
              splitters, oversized wheels or modifications that may affect loading. Tilt-bed and low-loading
              transport equipment may be available, but suitability depends on the individual vehicle and
              transporter.
            </P>
          </Section>

          <Section n={9} title="Vehicle Access">
            <P>
              The customer is responsible for providing accurate information regarding access at collection
              and delivery. A transporter requires sufficient legal and safe access for loading and unloading.
            </P>
            <P>
              Where access is restricted, unsafe or unsuitable, the customer may be required to arrange a
              safe nearby meeting point. MHJ and its drivers are not required to enter roads, driveways or
              locations where doing so would reasonably risk damage, obstruction, illegality or unsafe
              operation.
            </P>
          </Section>

          <Section n={10} title="Auction, Salvage and Storage Collections">
            <P>Customers are responsible for supplying any required:</P>
            <UL items={[
              "Release / collection reference",
              "Buyer number",
              "Identification requirement",
              "Authority to collect",
              "Storage-yard documentation",
            ]}/>
            <P>
              Reasonable waiting/loading time of up to 30 minutes is included. Where delays outside the
              transporter&apos;s reasonable control exceed 30 minutes, a waiting charge of £25 per additional
              30-minute period may apply where reasonably incurred.
            </P>
          </Section>

          <Section n={11} title="Vehicle Contents">
            <P>
              Unless expressly agreed beforehand, the service relates to transportation of the vehicle.
              Customers should remove valuable, fragile, illegal, hazardous or prohibited items before
              collection. MHJ may refuse to carry items that could create an unreasonable safety, legal or
              insurance risk.
            </P>
          </Section>

          <Section n={12} title="Fuel, Keys and Preparation">
            <P>The customer should ensure that:</P>
            <UL items={[
              "Required keys are available",
              "Alarm/immobiliser information is available where necessary",
              "The vehicle is accessible",
              "Any relevant security device can be disabled for transport",
              "The vehicle contains sufficient fuel or battery charge where represented as a runner",
            ]}/>
          </Section>

          <Section n={13} title="Existing Damage">
            <P>
              Vehicle condition may be recorded at collection using photographs, video or written records.
              Customers should disclose known significant damage where it may affect loading or transport.
              Existing damage is not the responsibility of MHJ. Nothing in these terms excludes liability
              for damage caused by a failure by MHJ or a transporter for which MHJ is legally responsible.
            </P>
          </Section>

          <Section n={14} title="Subcontracted Transport">
            <P>
              MHJ may use appropriately selected third-party transporters or subcontractors to perform some
              transport services. MHJ may provide those transporters with information reasonably necessary
              to complete the job, including customer name, contact details, collection and delivery
              information, vehicle details and booking instructions.
            </P>
            <P>The customer&apos;s contractual and statutory rights remain unaffected.</P>
          </Section>

          <Section n={15} title="Insurance">
            <P>
              Transport will be carried out subject to the applicable transporter&apos;s insurance
              arrangements and the terms of any relevant policy. Customers should notify MHJ promptly of any
              alleged transport damage and provide supporting evidence where reasonably requested.
            </P>
          </Section>

          <Section n={16} title="Customer Availability">
            <P>
              The customer must ensure that either they or an authorised representative is available at
              collection and delivery unless alternative arrangements have been agreed. Where keys are being
              left with a third party, auction, dealership, reception or storage facility, the customer must
              ensure MHJ is authorised to collect them.
            </P>
          </Section>

          <Section n={17} title="Failed Collections">
            <P>If collection cannot take place because of circumstances caused by incorrect information or failure by the customer, including:</P>
            <UL items={[
              "Vehicle unavailable",
              "No keys",
              "Vehicle not released",
              "Customer cannot be contacted",
              "Transporter denied access",
              "Vehicle condition materially misdescribed",
            ]}/>
            <P>MHJ may charge reasonable costs actually incurred as a result.</P>
          </Section>

          <Section n={18} title="Payment of Balance">
            <P>The remaining balance becomes payable in accordance with the payment arrangement stated in the booking confirmation. The customer will be told the amount outstanding before completion.</P>
          </Section>

          <Section n={19} title="Changes to a Booking">
            <P>Customers should contact MHJ as soon as possible if they need to change:</P>
            <UL items={[
              "Collection date, location or vehicle",
              "Delivery location",
              "Vehicle condition or requested transport service",
            ]}/>
            <P>Where a requested change materially affects distance, cost, driver allocation or transport requirements, MHJ may provide a revised quotation.</P>
          </Section>

          <Section n={20} title="Events Outside Reasonable Control">
            <P>
              MHJ will not be treated as responsible for delay caused entirely by events outside reasonable
              control where reasonable steps have been taken to minimise the impact. This may include severe
              weather, road closures, accidents, public authority action or other major disruption.
            </P>
          </Section>

          <Section n={21} title="Complaints">
            <P>
              If a customer has a problem with a transport service, they should contact MHJ as soon as
              reasonably possible so the issue can be investigated at{" "}
              <a className="underline" href={`mailto:${BRAND.email}`}>{BRAND.email}</a> or{" "}
              <a className="underline" href={`tel:${BRAND.phoneRaw}`}>{BRAND.phone}</a>. MHJ will aim to
              resolve complaints fairly and reasonably.
            </P>
          </Section>

          <Section n={22} title="Consumer Rights">
            <P>Nothing in these Terms affects rights provided to consumers by UK law. Services will be provided with reasonable care and skill.</P>
          </Section>

          <Section n={23} title="Personal Information">
            <P>MHJ uses customer information for producing quotations, processing bookings, arranging transport, processing payments, contacting customers, allocating drivers or subcontractors, administering bookings and handling queries and complaints.</P>
            <P>
              See our <Link to="/privacy" className="underline">Privacy Policy</Link> for full details.
            </P>
          </Section>

          <Section n={24} title="Governing Law">
            <P>These Terms are governed by the applicable laws of England and Wales unless mandatory consumer law provides otherwise.</P>
          </Section>

          <Section n={25} title="Changes to These Terms">
            <P>MHJ may update these Terms from time to time. The version applicable to a booking will normally be the version presented to and accepted by the customer when that booking was made.</P>
          </Section>

          <Section n={26} title="Contact Information">
            <P>MHJ Prestige Transport Ltd</P>
            <UL items={[
              `Email: ${BRAND.email}`,
              `Phone: ${BRAND.phone}`,
              `Location: ${BRAND.location}`,
            ]}/>
            <P className="text-xs">Full registered company details will be added here.</P>
          </Section>

          <div className="mt-16 pt-8 border-t border-white/10 text-xs text-zinc-500">
            Version 1.0 · Last updated {new Date().toLocaleDateString("en-GB")}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
